import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, Firestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, FirebaseStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, Messaging } from 'firebase/messaging';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase only once
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;
let storage: FirebaseStorage;
let functions: Functions;
let messaging: Messaging;

if (!getApps().length) {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);

  // Initialize Firestore with persistent cache to allow offline booting.
  // ignoreUndefinedProperties drops `undefined` fields instead of throwing —
  // prevents "Unsupported field value: undefined" on partial writes/merges.
  db = initializeFirestore(app, {
    localCache: persistentLocalCache({
      tabManager: persistentMultipleTabManager()
    }),
    ignoreUndefinedProperties: true,
  });

  storage = getStorage(app);
  functions = getFunctions(app);

  const isJSDOM = typeof navigator !== 'undefined' && navigator.userAgent && navigator.userAgent.includes('jsdom');
  if (typeof window !== 'undefined' && !isJSDOM) {
    try {
      messaging = getMessaging(app);
    } catch (e) {
      console.warn('Firebase messaging not supported in this environment', e);
    }
  }

  console.log('Firebase initialized successfully with offline persistence');
} else {
  app = getApps()[0];
  auth = getAuth(app);
  db = getFirestore(app); // Use existing instead of re-initializing if already done
  storage = getStorage(app);
  functions = getFunctions(app);

  const isJSDOM = typeof navigator !== 'undefined' && navigator.userAgent && navigator.userAgent.includes('jsdom');
  if (typeof window !== 'undefined' && !isJSDOM) {
    try {
      messaging = getMessaging(app);
    } catch (e) {
      console.warn('Firebase messaging not supported in this environment', e);
    }
  }
}

// Export initialized services
export { app, auth, db, storage, functions, messaging };

// Connect to Firebase Emulators in dev mode
// This enables safe local testing of Security Rules, Auth, and Storage
//
// NOTE: connectXEmulator() only rewrites the SDK's endpoints — it does NOT
// verify the emulator is running and cannot fall back to remote Firebase.
// With VITE_USE_EMULATOR=true and a dead emulator, every call fails at
// request time (callables surface as opaque `functions/internal` errors).
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATOR === 'true' && !('__EMULATORS_CONNECTED__' in window)) {
  try {
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    (window as any).__EMULATORS_CONNECTED__ = true;
    console.log('🔧 Connected to Firebase Emulators (Auth:9099, Firestore:8080, Storage:9199, Functions:5001)');
  } catch (e) {
    // Only reachable if a connect call itself throws (e.g. SDK already used)
    console.error('⚠️ Failed to wire Firebase Emulators — SDK may already be initialized', e);
  }

  // Health ping: fail loudly now instead of as cryptic errors on first use
  fetch('http://localhost:5001/', { mode: 'no-cors' }).catch(() => {
    console.error(
      '⚠️ VITE_USE_EMULATOR=true but no functions emulator is listening on localhost:5001. '
      + 'ALL Cloud Function calls will fail. Run `firebase emulators:start` '
      + 'or set VITE_USE_EMULATOR=false to use remote Firebase.'
    );
  });
}
